import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Upload,
  Scan,
  FileText,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
} from "lucide-react";
import type { OCRResult } from "@/types/expenses";

interface OCRProcessorProps {
  onResult: (data: Partial<OCRResult>) => void;
  onCancel: () => void;
}

type ProcessingStatus = 'idle' | 'loading' | 'processing' | 'success' | 'error';

export function OCRProcessor({ onResult, onCancel }: OCRProcessorProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [status, setStatus] = useState<ProcessingStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<Partial<OCRResult> | null>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Formato no soportado. Usa JPG, PNG, WebP o PDF.');
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 10MB.');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setExtractedData(null);

    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  }, []);

  const processOCR = useCallback(async () => {
    if (!file) return;

    setStatus('loading');
    setProgress(0);
    setError(null);

    try {
      // Dynamic import of Tesseract.js to avoid loading it on page load
      setProgress(10);
      let Tesseract;
      try {
        Tesseract = await import('tesseract.js');
      } catch (importError) {
        throw new Error(
          'Tesseract.js no está instalado. Ejecuta: npm install tesseract.js'
        );
      }

      setStatus('processing');
      setProgress(20);

      // Process the image
      const result = await Tesseract.recognize(file, 'spa', {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(20 + Math.round(m.progress * 70));
          }
        },
      });

      setProgress(90);

      const text = result.data.text;
      const extracted = parseInvoiceText(text);

      setExtractedData({
        ...extracted,
        raw_text: text,
        confidence: result.data.confidence,
      });

      setProgress(100);
      setStatus('success');

    } catch (err: any) {
      console.error('OCR Error:', err);
      setError(err.message || 'Error procesando la imagen');
      setStatus('error');
    }
  }, [file]);

  const handleConfirm = () => {
    if (extractedData) {
      onResult(extractedData);
    }
  };

  return (
    <div className="space-y-4">
      {/* File Upload */}
      {status === 'idle' && (
        <div className="space-y-4">
          <div
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors hover:border-primary hover:bg-muted/50
              ${file ? 'border-primary bg-muted/30' : 'border-muted-foreground/25'}
            `}
            onClick={() => document.getElementById('ocr-file-input')?.click()}
          >
            <input
              id="ocr-file-input"
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="hidden"
            />

            {!file ? (
              <>
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">
                  Arrastra o selecciona una imagen
                </p>
                <p className="text-sm text-muted-foreground">
                  JPG, PNG, WebP o PDF (máx. 10MB)
                </p>
              </>
            ) : (
              <div className="flex items-center justify-center gap-4">
                {preview ? (
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-h-32 rounded border"
                  />
                ) : (
                  <FileText className="w-12 h-12 text-muted-foreground" />
                )}
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-7 px-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setPreview(null);
                    }}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Cambiar
                  </Button>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button onClick={processOCR} disabled={!file}>
              <Scan className="w-4 h-4 mr-2" />
              Procesar OCR
            </Button>
          </div>
        </div>
      )}

      {/* Processing */}
      {(status === 'loading' || status === 'processing') && (
        <div className="space-y-4 py-8">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-primary animate-spin" />
            <p className="text-lg font-medium">
              {status === 'loading' ? 'Cargando motor OCR...' : 'Extrayendo texto...'}
            </p>
          </div>
          <Progress value={progress} className="w-full" />
          <p className="text-center text-sm text-muted-foreground">
            {progress}% completado
          </p>
        </div>
      )}

      {/* Error */}
      {status === 'error' && (
        <div className="space-y-4 py-8">
          <div className="flex flex-col items-center gap-4">
            <AlertCircle className="w-12 h-12 text-destructive" />
            <p className="text-lg font-medium text-destructive">
              Error al procesar
            </p>
            <p className="text-sm text-muted-foreground">
              {error || 'No se pudo extraer el texto de la imagen'}
            </p>
          </div>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button onClick={() => setStatus('idle')}>
              Intentar de nuevo
            </Button>
          </div>
        </div>
      )}

      {/* Success - Show extracted data */}
      {status === 'success' && extractedData && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-green-600 mb-4">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">
              Datos extraídos (confianza: {extractedData.confidence?.toFixed(0)}%)
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Proveedor detectado</Label>
              <Input
                value={extractedData.supplier_name || ''}
                onChange={(e) => setExtractedData({
                  ...extractedData,
                  supplier_name: e.target.value
                })}
                placeholder="No detectado"
              />
            </div>
            <div className="space-y-2">
              <Label>NIF/CIF</Label>
              <Input
                value={extractedData.supplier_tax_id || ''}
                onChange={(e) => setExtractedData({
                  ...extractedData,
                  supplier_tax_id: e.target.value
                })}
                placeholder="No detectado"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input
                type="date"
                value={extractedData.date || ''}
                onChange={(e) => setExtractedData({
                  ...extractedData,
                  date: e.target.value
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>Nº Factura</Label>
              <Input
                value={extractedData.invoice_number || ''}
                onChange={(e) => setExtractedData({
                  ...extractedData,
                  invoice_number: e.target.value
                })}
                placeholder="No detectado"
              />
            </div>
            <div className="space-y-2">
              <Label>IVA %</Label>
              <Input
                type="number"
                value={extractedData.tax_rate || 21}
                onChange={(e) => setExtractedData({
                  ...extractedData,
                  tax_rate: parseFloat(e.target.value) || 21
                })}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Base imponible</Label>
              <Input
                type="number"
                step="0.01"
                value={extractedData.base_amount || 0}
                onChange={(e) => setExtractedData({
                  ...extractedData,
                  base_amount: parseFloat(e.target.value) || 0
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>IVA</Label>
              <Input
                type="number"
                step="0.01"
                value={extractedData.tax_amount || 0}
                onChange={(e) => setExtractedData({
                  ...extractedData,
                  tax_amount: parseFloat(e.target.value) || 0
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>Total</Label>
              <Input
                type="number"
                step="0.01"
                value={extractedData.total_amount || 0}
                onChange={(e) => setExtractedData({
                  ...extractedData,
                  total_amount: parseFloat(e.target.value) || 0
                })}
              />
            </div>
          </div>

          {extractedData.raw_text && (
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Ver texto extraído
              </summary>
              <pre className="mt-2 p-3 bg-muted rounded text-xs whitespace-pre-wrap max-h-32 overflow-y-auto">
                {extractedData.raw_text}
              </pre>
            </details>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setStatus('idle')}>
              Volver a escanear
            </Button>
            <Button onClick={handleConfirm}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Usar estos datos
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Parse extracted text to find invoice/receipt data
 */
function parseInvoiceText(text: string): Partial<OCRResult> {
  const result: Partial<OCRResult> = {};

  // Normalize text
  const normalized = text.toUpperCase();
  const lines = normalized.split('\n').map(l => l.trim()).filter(Boolean);

  // Find NIF/CIF (Spanish tax ID patterns)
  const nifMatch = normalized.match(/(?:NIF|CIF|N\.?I\.?F\.?|C\.?I\.?F\.?)[:\s]*([A-Z]?\d{7,8}[A-Z]?)/);
  if (nifMatch) {
    result.supplier_tax_id = nifMatch[1];
  } else {
    // Try standalone pattern
    const standaloneNif = normalized.match(/\b([ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J])\b/);
    if (standaloneNif) {
      result.supplier_tax_id = standaloneNif[1];
    }
  }

  // Find date (DD/MM/YYYY or DD-MM-YYYY)
  const dateMatch = normalized.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
  if (dateMatch) {
    let [, day, month, year] = dateMatch;
    if (year.length === 2) {
      year = '20' + year;
    }
    result.date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  // Find invoice number
  const invoiceMatch = normalized.match(/(?:FACTURA|FACT\.?|FA|N[ºO°]?)[:\s]*([A-Z0-9\-\/]+)/);
  if (invoiceMatch) {
    result.invoice_number = invoiceMatch[1];
  }

  // Find amounts - look for TOTAL, BASE, IVA patterns
  const amountRegex = /(\d{1,3}(?:[.,]\d{3})*[.,]\d{2})/g;
  const amounts: number[] = [];
  let match;
  while ((match = amountRegex.exec(normalized)) !== null) {
    const amount = parseFloat(match[1].replace(/\./g, '').replace(',', '.'));
    if (amount > 0 && amount < 100000) {
      amounts.push(amount);
    }
  }

  // Try to identify base, tax, and total
  const totalMatch = normalized.match(/TOTAL[:\s]*(\d{1,3}(?:[.,]\d{3})*[.,]\d{2})/);
  if (totalMatch) {
    result.total_amount = parseFloat(totalMatch[1].replace(/\./g, '').replace(',', '.'));
  }

  const baseMatch = normalized.match(/(?:BASE|IMPONIBLE|SUBTOTAL)[:\s]*(\d{1,3}(?:[.,]\d{3})*[.,]\d{2})/);
  if (baseMatch) {
    result.base_amount = parseFloat(baseMatch[1].replace(/\./g, '').replace(',', '.'));
  }

  const ivaMatch = normalized.match(/IVA[:\s]*(\d{1,2})[%\s]/);
  if (ivaMatch) {
    result.tax_rate = parseInt(ivaMatch[1]);
  }

  const ivaAmountMatch = normalized.match(/(?:IVA|I\.V\.A\.)[:\s]*(\d{1,3}(?:[.,]\d{3})*[.,]\d{2})/);
  if (ivaAmountMatch) {
    result.tax_amount = parseFloat(ivaAmountMatch[1].replace(/\./g, '').replace(',', '.'));
  }

  // If we have total but not base, try to calculate
  if (result.total_amount && !result.base_amount && result.tax_rate) {
    result.base_amount = Math.round(result.total_amount / (1 + result.tax_rate / 100) * 100) / 100;
  }

  // If we found amounts but couldn't parse them, use the largest as total
  if (!result.total_amount && amounts.length > 0) {
    result.total_amount = Math.max(...amounts);
    if (result.tax_rate) {
      result.base_amount = Math.round(result.total_amount / (1 + result.tax_rate / 100) * 100) / 100;
    }
  }

  // Try to find supplier name (usually one of the first non-numeric lines)
  for (const line of lines.slice(0, 5)) {
    if (
      line.length > 5 &&
      line.length < 50 &&
      !line.match(/^\d/) &&
      !line.match(/FACTURA|TICKET|RECIBO|NIF|CIF|FECHA|TOTAL|IVA|TEL|WWW|HTTP|EMAIL/)
    ) {
      result.supplier_name = line
        .split(/[,\-]/, 1)[0]
        .trim()
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
      break;
    }
  }

  return result;
}
